import React from 'react';
import { X, CheckCircle2, Circle, ChevronDown, ChevronRight } from 'lucide-react';
import { roadmapData } from '../roadmap';

interface TodoListProps {
  isOpen: boolean;
  onClose: () => void;
  taskStatus: Record<string, 'done' | 'todo'>;
  onToggleTask: (taskId: string) => void;
}

const TodoList: React.FC<TodoListProps> = ({
  isOpen,
  onClose,
  taskStatus,
  onToggleTask,
}) => {
  const [expandedPhases, setExpandedPhases] = React.useState<Record<string, boolean>>({
    'phase-1': true,
    'phase-2': true,
    'phase-3': true,
    'phase-4': true,
  });

  const togglePhase = (phaseId: string) => {
    setExpandedPhases(prev => ({
      ...prev,
      [phaseId]: !prev[phaseId],
    }));
  };

  const getPhaseProgress = (phaseId: string) => {
    const phase = roadmapData.find(p => p.id === phaseId);
    if (!phase) return 0;
    const total = phase.tasks.length;
    const completed = phase.tasks.filter(t => taskStatus[t.id] === 'done').length;
    return Math.round((completed / total) * 100);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white">Development Roadmap</h2>
            <p className="text-sm text-gray-400">Track progress and features</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {roadmapData.map((phase) => {
            const progress = getPhaseProgress(phase.id);
            const isExpanded = expandedPhases[phase.id];

            return (
              <div key={phase.id} className="border border-gray-700 rounded-lg overflow-hidden">
                {/* Phase Header */}
                <button
                  onClick={() => togglePhase(phase.id)}
                  className="w-full flex items-center justify-between p-3 bg-gray-700/50 hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                    <span className="font-semibold text-white">{phase.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      phase.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                      phase.status === 'in-progress' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-gray-600 text-gray-400'
                    }`}>
                      {phase.status === 'completed' ? 'Completed' :
                       phase.status === 'in-progress' ? 'In Progress' : 'Planned'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-400">{progress}%</span>
                    <div className="w-20 h-2 bg-gray-600 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                </button>

                {/* Phase Tasks */}
                {isExpanded && (
                  <div className="p-2 space-y-1">
                    {phase.tasks.map((task) => {
                      const isDone = taskStatus[task.id] === 'done';
                      const isFixed = task.status === 'done';

                      return (
                        <button
                          key={task.id}
                          onClick={() => !isFixed && onToggleTask(task.id)}
                          disabled={isFixed}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors text-left ${
                            isFixed
                              ? 'opacity-60 cursor-default'
                              : 'hover:bg-gray-700/50 cursor-pointer'
                          }`}
                        >
                          {isDone || isFixed ? (
                            <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                          ) : (
                            <Circle className="w-5 h-5 text-gray-500 flex-shrink-0" />
                          )}
                          <span className={`text-sm ${isDone || isFixed ? 'text-gray-400 line-through' : 'text-gray-200'}`}>
                            {task.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 bg-gray-800">
          <div className="flex justify-between items-center text-sm text-gray-400">
            <span>
              {Object.values(taskStatus).filter(s => s === 'done').length} / {Object.values(taskStatus).length} tasks completed
            </span>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodoList;
